import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import './UserAdmin.css';

function UserAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeletedOnly, setShowDeletedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    profile: 'club',
    city: '',
    state: '',
    country: '',
    penis_size: '',
    password: '', // Only used for new users
    password_confirmation: '', // Password confirmation
  });

  const loadUserCount = useCallback(async () => {
    try {
      // Get a limited response just to check count
      const response = await usersApi.getAll(showDeletedOnly);
      let loadedUsers = [];
      if (response.data) {
        if (response.data.success && response.data.data) {
          loadedUsers = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          loadedUsers = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          loadedUsers = response.data.data;
        }
      }
      setUserCount(loadedUsers.length);
    } catch (err) {
      console.error('Error loading user count:', err);
    }
  }, [showDeletedOnly]);

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.profile !== 'admin') {
        navigate('/profile');
        return;
      }
      // Don't load users on mount - wait for search or explicit request
      // Just get the count to determine if we should show the list
      loadUserCount();
    }
  }, [user, authLoading, navigate, loadUserCount]);

  const loadUsers = async (overrideShowDeleted = null) => {
    try {
      setLoading(true);
      setError('');
      const currentShowDeleted = overrideShowDeleted !== null ? overrideShowDeleted : showDeletedOnly;
      const response = await usersApi.getAll(currentShowDeleted);
      
      let loadedUsers = [];
      if (response.data) {
        if (response.data.success && response.data.data) {
          loadedUsers = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (Array.isArray(response.data)) {
          loadedUsers = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          loadedUsers = response.data.data;
        }
      }
      
      // Limit to 20 rows
      const limitedUsers = loadedUsers.slice(0, 20);
      setUsers(limitedUsers);
      
      // Apply search filter if active
      if (searchTerm && searchTerm.trim() !== '') {
        applySearchFilter(limitedUsers, searchTerm);
      } else {
        setFilteredUsers(limitedUsers);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.message || 'Failed to load users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const applySearchFilter = (usersList, term) => {
    const searchLower = term.toLowerCase().trim();
    const filtered = usersList.filter(user => {
      const searchableFields = [
        user.fname || '',
        user.lname || '',
        user.email || '',
        user.profile || '',
        user.city || '',
        user.state || '',
        user.country || '',
        user.penis_size || '',
        `${user.fname || ''} ${user.lname || ''}`.trim(), // Full name
      ];
      
      return searchableFields.some(field => 
        field.toLowerCase().includes(searchLower)
      );
    });
    setFilteredUsers(filtered);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    
    if (term.trim() === '') {
      setFilteredUsers(users);
      setHasSearched(false);
      // Clear users if search is cleared and list is long
      if (userCount > 20) {
        setUsers([]);
        setFilteredUsers([]);
      }
    } else {
      setHasSearched(true);
      // Load users if we haven't loaded them yet
      if (users.length === 0) {
        loadUsers();
      } else {
        applySearchFilter(users, term);
      }
    }
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setHasSearched(false);
    // Clear users if list is long
    if (userCount > 20) {
      setUsers([]);
      setFilteredUsers([]);
    } else {
      setFilteredUsers(users);
    }
  };

  const handleToggleDeletedFilter = () => {
    const newShowDeleted = !showDeletedOnly;
    setShowDeletedOnly(newShowDeleted);
    setSearchTerm(''); // Clear search when toggling deleted filter
    setHasSearched(false);
    loadUsers(newShowDeleted);
    loadUserCount(); // Update count
  };

  const handleRestore = async (user) => {
    const id = user.guy_id || user.id;
    if (!id) {
      setError('Cannot restore user - ID not found');
      return;
    }
    
    try {
      setError('');
      await usersApi.restore(id);
      setSuccess('User restored successfully');
      loadUsers(showDeletedOnly); // Reload with current filter state
      loadUserCount(); // Update count
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error restoring user:', err);
      setError(err.response?.data?.message || 'Failed to restore user');
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      fname: '',
      lname: '',
      email: '',
      profile: 'club',
      city: '',
      state: '',
      country: '',
      penis_size: '',
      password: '',
      password_confirmation: '',
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleRowClick = (user, e) => {
    // Don't open modal if clicking on delete button or actions cell
    if (e.target.closest('.actions-cell') || e.target.closest('.btn-delete')) {
      return;
    }
    
    setEditingUser(user);
    setFormData({
      fname: user.fname || '',
      lname: user.lname || '',
      email: user.email || '',
      profile: user.profile || 'club',
      city: user.city || '',
      state: user.state || '',
      country: user.country || '',
      penis_size: user.penis_size || '',
      password: '', // Don't pre-fill password
      password_confirmation: '', // Don't pre-fill password confirmation
    });
    setShowEditModal(true);
    setError('');
    setSuccess('');
  };

  // handleEdit is intentionally unused - it's kept for potential future use
  // const handleEdit = (user) => {
  //   handleRowClick(user, { target: { closest: () => null } });
  // };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    const id = userToDelete.guy_id || userToDelete.id;
    if (!id) {
      setError('Cannot delete user - ID not found');
      setShowDeleteModal(false);
      setUserToDelete(null);
      return;
    }
    
    try {
      setError('');
      setShowDeleteModal(false);
      await usersApi.delete(id);
      setSuccess('User deleted successfully (soft delete - recoverable)');
      loadUsers(showDeletedOnly); // Reload with current filter state
      loadUserCount(); // Update count
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setUserToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Client-side validation for password confirmation
    if (editingUser) {
      // For updates, password is optional
      if (formData.password && formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (formData.password && formData.password !== formData.password_confirmation) {
        setError('Passwords do not match');
        return;
      }
    } else {
      // For creates, password is required
      if (!formData.password || formData.password.trim() === '') {
        setError('Password is required for new users');
        return;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (formData.password !== formData.password_confirmation) {
        setError('Passwords do not match');
        return;
      }
    }

    try {
      const submitData = { ...formData };
      
      // Remove password_confirmation before sending (backend doesn't need it)
      delete submitData.password_confirmation;
      
      // Only include password if it's a new user or if password was provided
      if (editingUser && !submitData.password) {
        delete submitData.password; // Don't send empty password for updates
      }
      
      if (editingUser) {
        const id = editingUser.guy_id || editingUser.id;
        await usersApi.update(id, submitData);
        setSuccess('User updated successfully');
      } else {
        await usersApi.create(submitData);
        setSuccess('User created successfully');
      }
      
      setShowForm(false);
      setShowEditModal(false);
      // Reload users - loadUsers will handle search filter automatically
      loadUsers(showDeletedOnly);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving user:', err);
      if (err.response && err.response.status === 422) {
        const validationErrors = err.response.data.errors;
        let errorMessages = 'Validation errors:\n';
        for (const field in validationErrors) {
          errorMessages += `${field}: ${validationErrors[field].join(', ')}\n`;
        }
        setError(errorMessages);
      } else {
        setError(err.response?.data?.message || 'Failed to save user');
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setShowEditModal(false);
    setEditingUser(null);
    setFormData({
      fname: '',
      lname: '',
      email: '',
      profile: 'club',
      city: '',
      state: '',
      country: '',
      penis_size: '',
      password: '',
      password_confirmation: '',
    });
    setError('');
  };

  if (authLoading || !user) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="user-admin-page">
          <div className="admin-header">
            <div className="admin-header-top">
              <Link to="/admin" className="btn btn-secondary back-btn">
                <i className="bi bi-arrow-left"></i> Back to GR Admin
              </Link>
              <h1>User Admin</h1>
            </div>
            {!showForm && (
              <div className="admin-header-buttons">
                <button onClick={handleCreate} className="btn btn-primary">
                  <i className="bi bi-plus-circle"></i> Create New User
                </button>
                <button 
                  onClick={handleToggleDeletedFilter} 
                  className={`btn ${showDeletedOnly ? 'btn-warning' : 'btn-secondary'}`}
                  title={showDeletedOnly ? 'Show all users' : 'Show deleted users only'}
                >
                  <i className={`bi ${showDeletedOnly ? 'bi-arrow-counterclockwise' : 'bi-trash'}`}></i>
                  {showDeletedOnly ? ' Show All' : ' Show Deleted Only'}
                </button>
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {!showForm && (
            <div className="admin-search-container">
              <div className="admin-search">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search users by name, email, profile, city, state, country..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={handleSearchClear}
                    title="Clear search"
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                )}
              </div>
                      {searchTerm && (
                        <div className="admin-search-results">
                          Showing {filteredUsers.length} of {users.length} users {userCount > 20 && `(Total: ${userCount}+)`}
                        </div>
                      )}
                      {!searchTerm && userCount > 20 && (
                        <div className="admin-search-results">
                          Showing first 20 of {userCount}+ users. Use search to find specific users.
                        </div>
                      )}
            </div>
          )}

          {showForm ? (
            <div className="admin-form-container">
              <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fname">First Name *</label>
                    <input
                      type="text"
                      id="fname"
                      name="fname"
                      value={formData.fname}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lname">Last Name *</label>
                    <input
                      type="text"
                      id="lname"
                      name="lname"
                      value={formData.lname}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="profile">Profile *</label>
                    <select
                      id="profile"
                      name="profile"
                      value={formData.profile}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="club">Club</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="penis_size">Penis Size</label>
                    <input
                      type="text"
                      id="penis_size"
                      name="penis_size"
                      value={formData.penis_size}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    disabled={loading}
                    minLength={editingUser ? 0 : 8}
                    autoComplete="new-password"
                  />
                  <small>Must be at least 8 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="password_confirmation">
                    Confirm Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    required={!editingUser}
                    disabled={loading}
                    minLength={editingUser ? 0 : 8}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="users-admin-list">
              {!hasSearched && userCount > 20 && users.length === 0 ? (
                <div className="search-prompt">
                  <p className="search-prompt-text">
                    <i className="bi bi-search"></i> Enter a search term to find users, or click "Load All Users" to view the first 20 users.
                  </p>
                  <button 
                    onClick={() => {
                      setHasSearched(true);
                      loadUsers();
                    }}
                    className="btn btn-primary"
                  >
                    <i className="bi bi-list-ul"></i> Load All Users (First 20)
                  </button>
                </div>
              ) : loading ? (
                <Loading />
              ) : (searchTerm ? filteredUsers : users).length === 0 ? (
                <p className="no-users">
                  {searchTerm ? `No users found matching "${searchTerm}"` : 'No users found'}
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Profile</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Country</th>
                        <th>Penis Size</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(searchTerm ? filteredUsers : users).map((user) => (
                        <tr 
                          key={user.guy_id || user.id}
                          onClick={(e) => handleRowClick(user, e)}
                          className="user-row-clickable"
                        >
                          <td>{user.guy_id || user.id}</td>
                          <td>{user.fname || '-'}</td>
                          <td>{user.lname || '-'}</td>
                          <td>{user.email || '-'}</td>
                          <td>
                            <span className={`profile-badge profile-${user.profile || 'club'}`}>
                              {user.profile || 'club'}
                            </span>
                          </td>
                          <td>{user.city || '-'}</td>
                          <td>{user.state || '-'}</td>
                          <td>{user.country || '-'}</td>
                          <td>{user.penis_size || '-'}</td>
                          <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                            {showDeletedOnly ? (
                              <button
                                onClick={() => handleRestore(user)}
                                className="btn btn-sm btn-restore"
                                title="Restore"
                              >
                                <i className="bi bi-arrow-counterclockwise"></i>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleDeleteClick(user)}
                                className="btn btn-sm btn-delete-icon"
                                title="Delete User"
                              >
                                <i className="bi bi-trash-fill"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button 
                type="button" 
                className="modal-close-btn" 
                onClick={handleCancel}
                title="Close"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-fname">First Name *</label>
                    <input
                      type="text"
                      id="edit-fname"
                      name="fname"
                      value={formData.fname}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-lname">Last Name *</label>
                    <input
                      type="text"
                      id="edit-lname"
                      name="lname"
                      value={formData.lname}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-email">Email *</label>
                    <input
                      type="email"
                      id="edit-email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-profile">Profile *</label>
                    <select
                      id="edit-profile"
                      name="profile"
                      value={formData.profile}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      className="profile-select"
                    >
                      <option value="club">Club</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-city">City</label>
                    <input
                      type="text"
                      id="edit-city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-state">State</label>
                    <input
                      type="text"
                      id="edit-state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-country">Country</label>
                    <input
                      type="text"
                      id="edit-country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-penis_size">Penis Size</label>
                    <input
                      type="text"
                      id="edit-penis_size"
                      name="penis_size"
                      value={formData.penis_size}
                      onChange={handleInputChange}
                      disabled={loading}
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-password">
                    Password (leave blank to keep current)
                  </label>
                  <input
                    type="password"
                    id="edit-password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    disabled={loading}
                    minLength={0}
                    autoComplete="new-password"
                  />
                  <small>Must be at least 8 characters if provided</small>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-password_confirmation">
                    Confirm Password (leave blank to keep current)
                  </label>
                  <input
                    type="password"
                    id="edit-password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    disabled={loading}
                    minLength={0}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showDeleteModal}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.fname} ${userToDelete?.lname}" (${userToDelete?.email})? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}

export default UserAdmin;
